// Mock API layer for frontend-only data management using localStorage

export interface ContactSubmission {
  id: number;
  name: string;
  email: string;
  message: string;
  timestamp: number;
}

export interface TeamApplication {
  id: number;
  name: string;
  email: string;
  position: string;
  experience: string;
  timestamp: number;
}

export interface PrototypeData {
  messagingDemo: string[];
  groupSystemDemo: string[];
  fileManagerDemo: string[];
  libraryPreviewDemo: string[];
  facultyConnectionDemo: string[];
}

// Storage keys
const CONTACT_SUBMISSIONS_KEY = 'unilinkd_contact_submissions';
const TEAM_APPLICATIONS_KEY = 'unilinkd_team_applications';

// Simulate network delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Mock prototype data
const mockPrototypeData: PrototypeData = {
  messagingDemo: [
    "Hey! Did you finish the assignment for tomorrow?",
    "Yes, I just submitted it. Need help with anything?",
    "Could you share your notes from today's lecture?",
    "Sure! I'll send them right now.",
    "Thanks! You're a lifesaver!",
    "No problem! Let me know if you need anything else.",
  ],
  groupSystemDemo: [
    "Computer Science 101 Study Group",
    "Mathematics Advanced Topics",
    "Physics Lab Partners",
    "Chemistry Research Team",
    "Biology Study Circle",
    "Engineering Project Group",
  ],
  fileManagerDemo: [
    "Lecture Notes - Week 1",
    "Assignments - Semester 1",
    "Research Papers",
    "Lab Reports",
    "Study Materials",
    "Project Documents",
    "Exam Preparation",
  ],
  libraryPreviewDemo: [
    "Introduction to Computer Science",
    "Advanced Mathematics for Engineers",
    "Physics: Principles and Applications",
    "Organic Chemistry Fundamentals",
    "Biology: The Science of Life",
    "Engineering Design Handbook",
  ],
  facultyConnectionDemo: [
    "Dr. Sarah Johnson - Computer Science",
    "Prof. Michael Chen - Mathematics",
    "Dr. Emily Rodriguez - Physics",
    "Prof. David Kim - Chemistry",
    "Dr. Lisa Anderson - Biology",
    "Prof. James Wilson - Engineering",
  ],
};

// Contact Form API
export const contactFormApi = {
  async submit(data: { name: string; email: string; message: string }): Promise<ContactSubmission> {
    await delay(500); // Simulate network delay
    
    const submissions = contactFormApi.getAll();
    const newSubmission: ContactSubmission = {
      id: Date.now(),
      ...data,
      timestamp: Date.now(),
    };
    
    submissions.push(newSubmission);
    localStorage.setItem(CONTACT_SUBMISSIONS_KEY, JSON.stringify(submissions));
    
    return newSubmission;
  },

  getAll(): ContactSubmission[] {
    const data = localStorage.getItem(CONTACT_SUBMISSIONS_KEY);
    return data ? JSON.parse(data) : [];
  },

  async delete(id: number): Promise<void> {
    await delay(300);
    
    const submissions = contactFormApi.getAll();
    const filtered = submissions.filter(s => s.id !== id);
    localStorage.setItem(CONTACT_SUBMISSIONS_KEY, JSON.stringify(filtered));
  },
};

// Team Application API
export const teamApplicationApi = {
  async submit(data: { name: string; email: string; position: string; experience: string }): Promise<TeamApplication> {
    await delay(500); // Simulate network delay
    
    const applications = teamApplicationApi.getAll();
    const newApplication: TeamApplication = {
      id: Date.now(),
      ...data,
      timestamp: Date.now(),
    };
    
    applications.push(newApplication);
    localStorage.setItem(TEAM_APPLICATIONS_KEY, JSON.stringify(applications));
    
    return newApplication;
  },

  getAll(): TeamApplication[] {
    const data = localStorage.getItem(TEAM_APPLICATIONS_KEY);
    return data ? JSON.parse(data) : [];
  },

  async delete(id: number): Promise<void> {
    await delay(300);
    
    const applications = teamApplicationApi.getAll();
    const filtered = applications.filter(a => a.id !== id);
    localStorage.setItem(TEAM_APPLICATIONS_KEY, JSON.stringify(filtered));
  },
};

// Prototype Data API
export const prototypeDataApi = {
  async get(): Promise<PrototypeData> {
    await delay(300); // Simulate network delay
    return mockPrototypeData;
  },
};
